/**
 * Step 2: put each CSV column into a slot of the item — Input, Expected output,
 * Output or Metadata — by dragging it, or with the menu on the column. Placed
 * from the header first; columns in no slot are skipped. Which agent input a
 * field feeds is chosen per experiment, not here.
 */

import { useState, type DragEvent } from "react";
import { Option } from "@/components/ui/field";
import type { ParsedCsv } from "@/lib/csv";
import { assign, buildItem, type Mapping, type Slot } from "./csv-mapping";
import { ColumnList } from "./import/column-list";
import { profileColumns } from "./import/column-profile";
import { ItemPreview } from "./import/item-preview";
import { SlotZones } from "./import/slot-zones";
import { DRAG_TYPE } from "./import/targets";

interface Props {
  csv: ParsedCsv;
  mapping: Mapping;
  onChange: (mapping: Mapping) => void;
}

export function ImportMap({ csv, mapping, onChange }: Props) {
  // The column being dragged; drops of anything else (a file, text) are ignored.
  const [dragging, setDragging] = useState<number | null>(null);
  const profiles = profileColumns(csv);
  const skipped = mapping.columns.filter((c) => c.target === "skip").map((c) => c.column);
  const first = csv.rows[0];

  const startDrag = (index: number, e: DragEvent) => {
    setDragging(index);
    e.dataTransfer?.setData(DRAG_TYPE, String(index)); // Firefox starts no drag without data
  };
  const drop = (slot: Slot | "skip") => {
    if (dragging === null) return;
    const current = mapping.columns[dragging]?.target;
    // The item key already sits in Metadata: dropping it there again keeps it the key.
    if (!(slot === "metadata" && current === "key")) onChange(assign(mapping, dragging, slot));
    setDragging(null);
  };

  return (
    <div className="flex flex-col gap-5" onDragEnd={() => setDragging(null)}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-[15px] font-semibold">Map columns</h2>
          <span className="text-xs text-muted">
            Drag each column into a slot of the item, or pick one in its menu. Placed from the column names;
            change anything that is wrong.
          </span>
        </div>
        <Option
          type="checkbox"
          checked={mapping.parseJson}
          onChange={(e) => onChange({ ...mapping, parseJson: e.target.checked })}
        >
          Parse JSON values
        </Option>
      </div>
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <ColumnList
          mapping={mapping}
          profiles={profiles}
          rows={csv.rows.length}
          onAssign={(i, t) => onChange(assign(mapping, i, t))}
          onDragStart={startDrag}
          onDropBack={() => drop("skip")}
        />
        <div className="flex min-w-0 flex-col gap-3">
          <SlotZones
            mapping={mapping}
            onDrop={drop}
            onRemove={(i) => onChange(assign(mapping, i, "skip"))}
            onDragStart={startDrag}
          />
          {skipped.length > 0 && (
            <p role="status" aria-label="Skipped columns" className="text-xs text-muted">
              Not in a slot, so skipped: <span className="font-mono">{skipped.join(", ")}</span>
            </p>
          )}
        </div>
      </div>
      <ItemPreview item={first ? buildItem(first, mapping) : null} />
    </div>
  );
}
